import create from 'zustand';
import {
  getEntitlements,
  getUnlockedAnalyses,
  useQuestion as apiUseQuestion,
} from './entitlementsApi';
import { CREDIT_COSTS } from './creditCosts';
import { canStartFullReport as canStartFullReportPolicy } from './subscriptionPolicy';

// Default/initial state
const initialState = {
  // Which pricing model the backend is enforcing; the UI branches on it.
  // 'credits' = legacy credit system, 'simple' = Free/Plus + one-time reports.
  pricingModel: 'credits',

  // Plan info
  plan: null, // 'FREE' | 'PLUS' | null
  planActiveUntil: null,
  isSubscriptionActive: false,
  hasEverSubscribed: false,

  // Credits (legacy credit system; all zero under simple pricing)
  credits: {
    total: 0,        // monthly + pack
    monthly: 0,      // from monthly allotment
    pack: 0,         // purchased credits (never expire)
    monthlyLimit: 0, // tier's monthly allotment
    resetDate: null,
  },

  // Simple-pricing chat allowances (unused under credit pricing)
  freeQuestions: {
    allowance: 0,  // lifetime free chat questions granted
    remaining: 0,  // lifetime free chat questions left
  },
  dailyQuestionsRemaining: 0, // Plus fair-use questions left today

  fullReportQuota: {
    limit: 0,
    remaining: 0,
    resetsAt: null,
  },

  // Unlocked analyses
  unlockedAnalyses: {
    birthCharts: [],
    relationships: [],
  },

  // Horoscope access
  horoscopeAccess: {
    daily: false,
    weekly: true,
    monthly: true,
  },

  // Toast notification
  toast: { message: '', type: 'info', isVisible: false },

  // Loading states
  isLoading: false,
  error: null,
  lastFetched: null,
};

const spendCreditsLocally = (credits, cost) => {
  const safeCost = Math.max(0, cost || 0);
  const monthly = Math.max(0, credits?.monthly || 0);
  const pack = Math.max(0, credits?.pack || 0);
  const monthlySpent = Math.min(monthly, safeCost);
  const remainingCost = safeCost - monthlySpent;
  const packSpent = Math.min(pack, remainingCost);

  return {
    ...(credits || {}),
    total: Math.max(0, (credits?.total || 0) - safeCost),
    monthly: monthly - monthlySpent,
    pack: pack - packSpent,
  };
};

const useEntitlementsStore = create((set, get) => ({
  ...initialState,

  // ========== ACTIONS ==========

  showToast: (message, type = 'info') => {
    set({ toast: { message, type, isVisible: true } });
    setTimeout(() => {
      set((state) => state.toast.message === message
        ? { toast: { ...state.toast, isVisible: false } }
        : state
      );
    }, 4000);
  },

  dismissToast: () => {
    set({ toast: { message: '', type: 'info', isVisible: false } });
  },

  /**
   * Fetch entitlements and unlocked analyses from backend
   */
  fetchEntitlements: async (userId) => {
    if (!userId) {
      console.warn('fetchEntitlements called without userId');
      return;
    }

    set({ isLoading: true, error: null });

    try {
      // Fetch both entitlements and unlocked analyses in parallel
      const [entitlementsData, unlockedData] = await Promise.all([
        getEntitlements(userId),
        getUnlockedAnalyses(userId),
      ]);

      // Parse entitlements response
      const plan = entitlementsData?.plan || 'FREE';
      // Map 'premium' to 'plus' for legacy support
      const normalizedPlan = plan.toUpperCase() === 'PREMIUM' ? 'PLUS' : plan.toUpperCase();
      const isPlus = normalizedPlan === 'PLUS';

      set({
        pricingModel: entitlementsData?.pricingModel || 'credits',
        plan: normalizedPlan,
        planActiveUntil: entitlementsData?.planActiveUntil
          ? new Date(entitlementsData.planActiveUntil)
          : null,
        isSubscriptionActive: entitlementsData?.isSubscriptionActive || false,
        hasEverSubscribed: entitlementsData?.hasEverSubscribed || false,

        credits: {
          total: entitlementsData?.credits?.total || 0,
          monthly: entitlementsData?.credits?.monthly || 0,
          pack: entitlementsData?.credits?.pack || 0,
          monthlyLimit: entitlementsData?.credits?.monthlyLimit || 0,
          resetDate: entitlementsData?.credits?.resetDate
            ? new Date(entitlementsData.credits.resetDate)
            : null,
        },

        freeQuestions: {
          allowance: entitlementsData?.freeQuestionsAllowance ?? 0,
          remaining: entitlementsData?.freeQuestionsRemaining ?? 0,
        },
        dailyQuestionsRemaining: entitlementsData?.dailyQuestionsRemaining ?? 0,

        fullReportQuota: {
          limit: entitlementsData?.fullReportQuota?.limit || 0,
          remaining: entitlementsData?.fullReportQuota?.remaining || 0,
          resetsAt: entitlementsData?.fullReportQuota?.resetsAt
            ? new Date(entitlementsData.fullReportQuota.resetsAt)
            : null,
        },

        horoscopeAccess: {
          daily: isPlus || entitlementsData?.horoscopeAccess?.daily || false,
          weekly: true, // Always available
          monthly: true, // Always available
        },

        // The backend returns a flat `unlocked` array of { entityType, entityId }.
        // Split it into per-type id lists that isAnalysisUnlocked() checks.
        unlockedAnalyses: (() => {
          const list = Array.isArray(unlockedData?.unlocked)
            ? unlockedData.unlocked
            : Array.isArray(unlockedData)
              ? unlockedData
              : [];
          return {
            birthCharts: list
              .filter((u) => u?.entityType === 'BIRTH_CHART')
              .map((u) => String(u.entityId)),
            relationships: list
              .filter((u) => u?.entityType === 'RELATIONSHIP')
              .map((u) => String(u.entityId)),
          };
        })(),

        isLoading: false,
        error: null,
        lastFetched: new Date(),
      });

      return { success: true };
    } catch (error) {
      console.error('Error fetching entitlements:', error);
      set({
        isLoading: false,
        error: error.message || 'Failed to fetch entitlements',
      });
      return { success: false, error: error.message };
    }
  },

  /**
   * Refresh entitlements after Stripe checkout return
   * Includes a delay to allow webhook processing
   */
  refreshAfterPurchase: async (userId, options = {}) => {
    // Back-compat: a plain number is the legacy delayMs-only call.
    const opts = typeof options === 'number' ? { delayMs: options } : (options || {});
    const {
      delayMs = 2000,
      intervalMs = 2000,
      maxAttempts = 5,
      until = null,
    } = opts;

    set({ isLoading: true });

    // Wait for the webhook to process, then fetch fresh data.
    await new Promise((resolve) => setTimeout(resolve, delayMs));
    let result = await get().fetchEntitlements(userId);

    // When a completion predicate is given (e.g. the purchased report is now
    // unlocked, or the plan is Plus), poll a few more times so a slow webhook
    // is caught without a manual refresh. Read-only; stops as soon as satisfied.
    if (typeof until === 'function') {
      let attempts = 1;
      while (attempts < maxAttempts && !until(get())) {
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
        result = await get().fetchEntitlements(userId);
        attempts += 1;
      }
    }

    return result;
  },

  /**
   * Use a question credit
   */
  useQuestion: async (userId) => {
    if (!userId) return { success: false, error: 'No user ID' };

    const simple = get().pricingModel === 'simple';
    let creditsSnapshot = null;
    try {
      set({ isLoading: true });
      // Optimistic spend only applies to the legacy credit wallet.
      if (!simple) creditsSnapshot = get().applyOptimisticCreditSpend(1);
      const result = await apiUseQuestion(userId);

      // Reconcile against backend state, but keep the optimistic deduction if refresh fails.
      try {
        await get().fetchEntitlements(userId);
      } catch (refreshError) {
        console.warn('Could not refresh entitlements after question usage:', refreshError);
        set({ isLoading: false });
      }

      if (simple) {
        const remaining = get().getChatQuestionsRemaining();
        get().showToast(`${remaining} question${remaining === 1 ? '' : 's'} left.`, 'info');
      } else {
        const updatedCredits = get().credits.total;
        get().showToast(`1 credit used. ${updatedCredits} remaining.`, 'info');
      }

      return { success: true, result };
    } catch (error) {
      if (creditsSnapshot) {
        get().restoreCredits(creditsSnapshot);
      }
      set({ isLoading: false, error: error.message });
      return { success: false, error: error.message };
    }
  },

  /**
   * Reset store to initial state (e.g., on logout)
   */
  reset: () => {
    set(initialState);
  },

  /**
   * Optimistically decrement credits so the UI updates immediately.
   * Returns a snapshot that can be restored if the chargeable action fails.
   */
  applyOptimisticCreditSpend: (cost) => {
    const state = get();

    if (state.credits.total < cost) {
      return null;
    }

    const previousCredits = { ...state.credits };
    set({
      credits: spendCreditsLocally(state.credits, cost),
    });

    return previousCredits;
  },

  /** Apply the authoritative billing result returned by a report workflow. */
  applyReportBilling: (billing) => {
    if (!billing) return;
    set((state) => ({
      fullReportQuota: {
        ...state.fullReportQuota,
        remaining: billing.quotaRemaining ?? state.fullReportQuota.remaining,
      },
      credits: {
        ...state.credits,
        pack: billing.packBalance ?? state.credits.pack,
        total: state.plan === 'PLUS'
          ? (billing.packBalance ?? state.credits.pack)
          : state.credits.total,
      },
    }));
  },

  /**
   * Restore credits from a snapshot after a failed optimistic deduction.
   */
  restoreCredits: (creditsSnapshot) => {
    if (!creditsSnapshot) return;

    set({
      credits: creditsSnapshot,
    });
  },

  // ========== HELPERS / SELECTORS ==========

  /** True when the backend is enforcing the simple (Free/Plus) model. */
  isSimplePricing: () => get().pricingModel === 'simple',

  /**
   * Chat questions the user has left right now. Under simple pricing this is
   * the Plus daily allowance (when subscribed) or the lifetime free allowance;
   * under credit pricing it falls back to the credit balance.
   */
  getChatQuestionsRemaining: () => {
    const state = get();
    if (state.pricingModel === 'simple') {
      return state.isPlusUser()
        ? state.dailyQuestionsRemaining
        : state.freeQuestions.remaining;
    }
    return state.credits.total;
  },

  /** Whether the user can ask at least one more chat question. */
  canAskQuestion: () => get().getChatQuestionsRemaining() > 0,

  /**
   * Check if user is on Plus plan (also accepts 'PREMIUM' for backward compatibility)
   */
  isPlusUser: () => {
    const state = get();
    const isPaidTier = state.plan === 'PLUS' || state.plan === 'PREMIUM';
    return isPaidTier && state.isSubscriptionActive;
  },

  /**
   * Check if user can access daily horoscope
   */
  canAccessDailyHoroscope: () => {
    const state = get();
    return state.horoscopeAccess.daily || state.isPlusUser();
  },

  /**
   * Check if a specific analysis is unlocked
   */
  isAnalysisUnlocked: (entityType, entityId) => {
    const state = get();
    if (entityType === 'BIRTH_CHART') {
      return state.unlockedAnalyses.birthCharts.includes(entityId);
    } else if (entityType === 'RELATIONSHIP') {
      return state.unlockedAnalyses.relationships.includes(entityId);
    }
    return false;
  },

  /**
   * Check if user can access 360 analysis for an entity
   * (Either Plus user with quota OR already purchased/unlocked)
   */
  canAccess360Analysis: (entityType, entityId) => {
    const state = get();

    // Check if already unlocked
    if (state.isAnalysisUnlocked(entityType, entityId)) {
      return true;
    }

    if (state.pricingModel === 'simple') {
      return state.canStartFullReport(entityType);
    }

    // Check if user has enough credits to unlock
    const cost = entityType === 'birthChart' ? CREDIT_COSTS.FULL_NATAL : CREDIT_COSTS.FULL_RELATIONSHIP;

    return state.credits.total >= cost;
  },

  /**
   * Check if user has enough credits
   */
  hasEnoughCredits: (cost) => {
    const state = get();
    return state.credits.total >= cost;
  },

  /**
   * Whether the user can start a full report WITHOUT paying now. Under simple
   * pricing that means Plus quota remains; otherwise they go through the
   * one-time purchase flow. Credit pricing uses the pooled quota then the
   * credit wallet.
   */
  canStartFullReport: (entityType) => {
    const state = get();
    if (state.pricingModel === 'simple') {
      return state.isPlusUser() && state.fullReportQuota.remaining > 0;
    }
    return canStartFullReportPolicy({
      entityType,
      isPlus: state.isPlusUser(),
      quotaRemaining: state.fullReportQuota.remaining,
      packCredits: state.credits.pack,
      totalCredits: state.credits.total,
    });
  },

  /**
   * Get current credit balance
   */
  getCredits: () => {
    const state = get();
    return state.credits;
  },

  /**
   * Get credits breakdown for display
   */
  getCreditsBreakdown: () => {
    const state = get();
    return {
      total: state.credits.total,
      monthly: state.credits.monthly,
      pack: state.credits.pack,
      monthlyLimit: state.credits.monthlyLimit,
      resetDate: state.credits.resetDate,
    };
  },

  /**
   * Check if user can create a relationship
   */
  canCreateRelationship: () => {
    const state = get();
    // Relationship overviews are free for everyone under simple pricing.
    if (state.pricingModel === 'simple') return true;
    return state.isPlusUser() || state.credits.total >= CREDIT_COSTS.RELATIONSHIP_OVERVIEW;
  },

  /**
   * Get credits reset date
   */
  getCreditsResetDate: () => {
    const state = get();
    return state.credits.resetDate;
  },
}));

export default useEntitlementsStore;
